import React, { useState, useMemo, useCallback } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useProjectAnalytics } from '@/hooks/useProjectAnalytics';
import { toActivitiesUrl } from '@/utils/drillthrough';
import { resolveHealthStatus } from '@/constants/analyticsThresholds';
import { formatDate, formatDaysWithUnit, formatPct, formatDeltaDays, formatDeltaCount } from '@/utils/analyticsFormat';
import { MetricsTable, type MetricsTableRow } from '@/components/analytics/MetricsTable';
import { MetricFocusPanel } from '@/components/analytics/MetricFocusPanel';
import { DataStatusPanel } from '@/components/analytics/DataStatusPanel';
import { EmptyState } from '@/components/analytics/EmptyState';
import { AnalyticsStatusRow } from '@/components/analytics/AnalyticsStatusRow';
import LoadingState from '@/components/ui/LoadingState';
import { showDiagnostics } from '@/lib/ui/diagnostics';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { TableScaffold } from '@/components/ui/TableScaffold';

const HealthPage: React.FC = () => {
  const { selectedProject, selectedBaselineId } = useGlobalProject();
  const { loading, merged, activities } = useProjectAnalytics();

  /* ── Local UI state for focus panel ── */
  const [selectedMetricIdx, setSelectedMetricIdx] = useState<number>(-1);

  const loaded = useMemo(
    () => ({
      project: false,
      schedule: !!merged,
      resources: false,
      risk: !!merged,
      activities: activities.length > 0,
      baseline: !!selectedBaselineId,
    }),
    [merged, activities.length, selectedBaselineId]
  );
  const errors = merged?.errors ?? [];

  const tableRows = useMemo((): MetricsTableRow[] => {
    if (!merged) return [];

    const {
      projectFinishDate,
      criticalPathDurationDays,
      scheduleMetrics,
      negativeFloatCount,
      criticalNearCriticalCount,
      highFloatPct,
      delta,
      dataSource,
    } = merged;

    const finishDisplay = formatDate(projectFinishDate);
    const finishDeltaStr = formatDeltaDays(delta?.projectFinishDays) ?? undefined;
    const cpDurationStr = formatDaysWithUnit(criticalPathDurationDays);
    const cpDurationDeltaStr = formatDeltaDays(delta?.criticalPathDurationDays) ?? undefined;
    const negFloatDeltaStr = formatDeltaCount(delta?.negativeFloatCount) ?? undefined;
    const isDerivedFinish = dataSource?.projectFinishDate === 'derived';
    const isDerivedCp = dataSource?.criticalPathDurationDays === 'derived';

    const criticalActivities = merged.criticalActivities ?? [];
    const worstFloatActivity = criticalActivities.length
      ? [...criticalActivities].sort((a, b) => a.floatDays - b.floatDays)[0]
      : null;
    const earliestNearCritical = criticalActivities.length
      ? [...criticalActivities]
          .filter((a) => a.criticalityBand === 'near-critical')
          .sort((a, b) => {
            const endA = (a as { early_end_date?: string }).early_end_date ?? '';
            const endB = (b as { early_end_date?: string }).early_end_date ?? '';
            return endA.localeCompare(endB);
          })[0]
      : null;
    const actId = (a: (typeof criticalActivities)[0]) =>
      (a as { id?: string }).id ?? (a as Record<string, unknown>).task_id ?? '';
    return [
      {
        metricName: 'Project finish vs target',
        currentValue: finishDisplay,
        delta: finishDeltaStr,
        status: resolveHealthStatus('finishDeltaDays', delta?.projectFinishDays ?? 0, {
          isFallback: isDerivedFinish,
          hasValue: projectFinishDate != null,
        }),
        viewHref: toActivitiesUrl({ critical: true }),
        viewLabel: `View ${criticalActivities.length} critical activities`,
        dataSourceHint: dataSource?.projectFinishDate ?? null,
      },
      {
        metricName: 'Critical path duration',
        currentValue: cpDurationStr,
        delta: cpDurationDeltaStr,
        status: isDerivedCp || criticalPathDurationDays == null ? 'unknown' : 'ok',
        viewHref: toActivitiesUrl({ critical: true }),
        viewLabel: `View ${criticalActivities.length} critical activities`,
        dataSourceHint: dataSource?.criticalPathDurationDays ?? null,
      },
      {
        metricName: 'Critical float remaining (min total float, days)',
        currentValue: formatDaysWithUnit(scheduleMetrics.minFloatDays),
        delta: undefined,
        status: resolveHealthStatus('minFloatDays', scheduleMetrics.minFloatDays),
        viewHref: toActivitiesUrl({ critical: true, sort: 'total_float_asc' }),
        viewLabel: 'View top by worst float',
        topContributor:
          worstFloatActivity
            ? {
                label: `Worst: ${actId(worstFloatActivity)}`,
                href: toActivitiesUrl({ critical: true, sort: 'total_float_asc' }),
              }
            : null,
      },
      {
        metricName: 'Negative float activities',
        currentValue: negativeFloatCount,
        delta: negFloatDeltaStr,
        status: resolveHealthStatus('negativeFloat', negativeFloatCount),
        viewHref: toActivitiesUrl({ negativeFloat: true }),
        viewLabel: `View ${negativeFloatCount} negative float activities`,
        topContributor:
          worstFloatActivity && worstFloatActivity.floatDays < 0
            ? {
                label: `Worst: ${actId(worstFloatActivity)}`,
                href: toActivitiesUrl({ negativeFloat: true, sort: 'total_float_asc' }),
              }
            : null,
      },
      {
        metricName: 'Critical + near-critical (float ≤5d)',
        currentValue: criticalNearCriticalCount,
        delta: `${scheduleMetrics.criticalCount} critical · ${scheduleMetrics.nearCriticalCount} near-crit`,
        status: resolveHealthStatus('criticalNearCritical', criticalNearCriticalCount),
        viewHref: '/app/analytics/critical-path',
        viewLabel: `View ${criticalNearCriticalCount} critical/near-critical`,
        topContributor:
          earliestNearCritical
            ? {
                label: `Earliest near-crit: ${actId(earliestNearCritical)}`,
                href: '/app/analytics/critical-path',
              }
            : null,
      },
      {
        metricName: 'Out-of-sequence',
        currentValue: '—',
        delta: undefined,
        status: 'unknown',
        viewHref: '/app/activities',
        viewLabel: 'View activities',
        dataSourceHint: null,
      },
      {
        metricName: 'High-float % (>44d)',
        currentValue: formatPct(highFloatPct),
        delta: undefined,
        status: resolveHealthStatus('highFloatPct', highFloatPct),
        viewHref: toActivitiesUrl({}),
        viewLabel: 'View all activities',
      },
    ];
  }, [merged]);

  const handleSelectRow = useCallback((idx: number) => {
    setSelectedMetricIdx((prev) => (prev === idx ? -1 : idx));
  }, []);

  const selectedRow = selectedMetricIdx >= 0 && selectedMetricIdx < tableRows.length
    ? tableRows[selectedMetricIdx]
    : null;

  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !merged) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading schedule health…" size="lg" fullScreen={false} />
      </div>
    );
  }

  const minFloatDays = merged?.scheduleMetrics?.minFloatDays ?? 0;
  const statStatus = (s: 'ok' | 'warning' | 'critical' | 'unknown'): 'ok' | 'warning' | 'critical' | 'neutral' =>
    s === 'unknown' ? 'neutral' : s;

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Key metrics"
        subtitle="Float, finish, and risk signals with drill-through."
        meta={<AnalyticsStatusRow loaded={loaded} errorCount={errors.length} errorMessages={errors} />}
      />

      {/* Signal strip: compact, subordinate, max 4 */}
      {merged && (
        <div className="grid grid-cols-4 gap-2">
          <StatCard
            label="Min float (days)"
            value={formatDaysWithUnit(minFloatDays)}
            status={statStatus(resolveHealthStatus('minFloatDays', minFloatDays))}
          />
          <StatCard
            label="Neg. float activities"
            value={merged.negativeFloatCount}
            delta={formatDeltaCount(merged.delta?.negativeFloatCount) ?? undefined}
            status={statStatus(resolveHealthStatus('negativeFloat', merged.negativeFloatCount))}
          />
          <StatCard
            label="Critical + Near-Critical (≤5d)"
            value={merged.criticalNearCriticalCount}
            delta={`${merged.scheduleMetrics.criticalCount} Critical · ${merged.scheduleMetrics.nearCriticalCount} Near-Crit`}
            status={statStatus(resolveHealthStatus('criticalNearCritical', merged.criticalNearCriticalCount))}
          />
          <StatCard
            label="High float % (>44d)"
            value={formatPct(merged.highFloatPct)}
            status={statStatus(resolveHealthStatus('highFloatPct', merged.highFloatPct))}
          />
        </div>
      )}

      {/* Main body: table + focus panel split */}
      <div className="grid grid-cols-12 gap-3 items-start">
        {/* Left: Metrics table (8 cols) */}
        <div className="col-span-12 lg:col-span-8">
          <TableScaffold
            toolbar={
              <div className="flex items-center justify-between w-full">
                <span className="text-xs font-medium text-muted-foreground">Key metrics</span>
                <span className="text-[10px] text-muted-foreground/60">{tableRows.length} metrics</span>
              </div>
            }
          >
            <MetricsTable
              rows={tableRows}
              loading={loading && !!merged}
              selectedIndex={selectedMetricIdx >= 0 ? selectedMetricIdx : undefined}
              onSelectRow={handleSelectRow}
            />
          </TableScaffold>
        </div>

        {/* Right: Focus panel (4 cols), sticky */}
        <div className="col-span-12 lg:col-span-4 lg:sticky lg:top-4">
          <MetricFocusPanel row={selectedRow} />
        </div>
      </div>

      {showDiagnostics() && (
        <DataStatusPanel loaded={loaded} errors={errors} className="mt-1" />
      )}
    </div>
  );
};

export default HealthPage;
